import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { pb } from '../lib/pocketbase'

interface CurrentUser {
  id: string
  name: string
  email: string
  role?: 'admin' | 'editor'
}

interface AuthContextValue {
  user: CurrentUser | null
  ready: boolean
  busy: boolean
  error: string
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  isAdmin: boolean
}

const Context = createContext<AuthContextValue | null>(null)

function toUser(record: typeof pb.authStore.record): CurrentUser | null {
  if (!record) return null
  return { id: record.id, name: record.name || record.email, email: record.email, role: record.role }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(() => toUser(pb.authStore.record))
  const [ready, setReady] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const unsubscribe = pb.authStore.onChange(() => setUser(toUser(pb.authStore.record)))
    let cancelled = false

    async function refreshSession() {
      if (pb.authStore.isValid) {
        try {
          await pb.collection('users').authRefresh()
        } catch {
          pb.authStore.clear()
        }
      }
      if (!cancelled) {
        setUser(toUser(pb.authStore.record))
        setReady(true)
      }
    }

    void refreshSession()
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  const login = async (email: string, password: string) => {
    setBusy(true)
    setError('')
    try {
      await pb.collection('users').authWithPassword(email, password)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível entrar.')
      throw cause
    } finally {
      setBusy(false)
    }
  }

  const logout = () => pb.authStore.clear()

  return <Context.Provider value={{ user, ready, busy, error, login, logout, isAdmin: user?.role === 'admin' }}>{children}</Context.Provider>
}

export function useAuth() {
  const value = useContext(Context)
  if (!value) throw new Error('useAuth precisa estar dentro de AuthProvider')
  return value
}
